import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/nav-link";
import { Menu, X, User, LogOut, ChevronDown, History } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/redux/hooks";
import { loginSignupSelector } from "@/components/login-signup/LoginSignupSlice";
import { fetchProfile, profileSelector } from "@/components/profile/profileSlice";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { logout } from "@/utils/logout";
import "./Header.scss";
import { motion } from "framer-motion";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { authenticated, user, token } = useAppSelector(loginSignupSelector);
  const { profile } = useAppSelector(profileSelector);

  // Fetch profile when authenticated to get latest data (avatar, etc.)
  useEffect(() => {
    if (authenticated && !profile) {
      dispatch(fetchProfile());
    }
  }, [authenticated, profile, dispatch]);

  // Prefer profile data if available, fallback to user (auth) data
  const displayUser = profile || user;

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about-us" },
    { name: "Community", path: "/community" },
    // { name: "Pricing", path: "/pricing" },
    ...(authenticated ? [
      { name: "Evaluate", path: "/evaluate" },
      { name: "History", path: "/history" }
    ] : [])
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/"); // Redirect to home page after logout
  };

  const getInitials = (email?: string, fullName?: string | null) => {
    if (fullName) {
      return fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return "U";
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white transition-all duration-300">
      <div className="container px-3 flex h-16 items-center justify-between">
        <div className="flex items-center gap-8">
          <NavLink to="/" className="flex flex-col items-start leading-none group">
            <h1 className="text-4xl flex gap-1 font-black tracking-tighter bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent pb-1">
             <img className="h-10 translate-y-1" src="/logoremovedbg.webp" alt="" /> parakh
            </h1>
            <span className="text-[8px] font-medium text-gray-500 translate-x-16 -translate-y-1 -mt-1 group-hover:text-gray-700 transition-colors">
              Powered by Kuberya AI
            </span>
          </NavLink>
        </div>

        <nav className="hidden md:flex flex-1 justify-center items-center gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 z-10 ${isActive ? "text-white" : "text-gray-500 hover:text-black hover:bg-gray-50"
                  }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="active-nav-pill"
                    className="absolute inset-0 bg-indigo-500 rounded-full shadow-md shadow-indigo-500/30 -z-10"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                {link.name}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {/* Mobile menu button - show first on mobile */}
          <Button
            onClick={() => setMobileMenuOpen(true)}
            variant="ghost"
            size="icon"
            className="bg-background/80 border rounded-md md:hidden"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle menu</span>
          </Button>

          {authenticated && token ? (
            // User is logged in - show avatar dropdown
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="group flex items-center gap-2 pr-2 pl-1 py-1 rounded-full border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 outline-none focus:ring-2 focus:ring-indigo-100 bg-white shadow-sm">
                  <Avatar className="h-9 w-9 border border-gray-100">
                    <AvatarImage className="object-cover" src={displayUser?.avatar || undefined} alt={displayUser?.email || "User"} />
                    <AvatarFallback className="bg-indigo-600 text-white text-xs font-bold">
                      {getInitials(displayUser?.email, displayUser?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:flex flex-col items-start text-left pl-1">
                    <span className="text-sm font-bold text-gray-700 leading-none group-hover:text-black transition-colors">{displayUser?.full_name?.split(' ')[0] || "Account"}</span>
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors ml-1" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 p-3 translate-x-1.5 rounded-[2rem] shadow-2xl border-0 ring-1 ring-black/5 md:-tranlate-x-0 -translate-x-1.5 bg-white mt-3" align="end" forceMount>

                {/* User Header Box */}
                <div className="flex items-center gap-4 p-4 mb-2 bg-gray-50/80 rounded-[1.2rem] border border-gray-100/50">
                  {/* <div className="h-12 w-12 shrink-0 rounded-full bg-[#7C3AED] flex items-center justify-center text-white font-bold text-xl shadow-sm">
                    {getInitials(displayUser?.email, displayUser?.full_name)}
                  </div> */}
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-base font-bold text-gray-900 truncate tracking-tight">{displayUser?.full_name || "User"}</span>
                    <span className="text-xs text-gray-500 truncate font-medium">{displayUser?.email}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-1 px-1">
                  <DropdownMenuItem
                    onClick={() => navigate("/profile")}
                    className="flex items-center gap-4 p-2 rounded-2xl text-[15px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 cursor-pointer outline-none transition-colors"
                  >
                    <User className="w-5 h-5 text-gray-400" />
                    My Profile
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => navigate("/history")}
                    className="flex items-center gap-4 p-2 rounded-2xl text-[15px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 cursor-pointer outline-none transition-colors"
                  >
                    <History className="w-5 h-5 text-gray-400" />
                    Evaluation History
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-4 p-2 rounded-2xl text-[15px] font-medium text-red-600 hover:bg-red-50/50 cursor-pointer outline-none transition-colors"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-red-50 text-red-500">
                      <LogOut className="w-4 h-4" />
                    </div>
                    Sign Out
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            // User is not logged in - show login button
            <Button
              onClick={() => navigate("/login")}
              className="bg-gradient-to-br from-purple-600 to-blue-500 border-0 text-white hover:shadow-lg hover:opacity-90 transition-all duration-300 rounded-full px-6 hidden md:flex"
            >
              Login / Sign Up
            </Button>
          )}
        </div>
      </div>

      {/* Mobile menu drawer */}
      <div
        className={`mobile-sidebar-overlay${mobileMenuOpen ? "" : " closed"}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div className={`mobile-sidebar-drawer${mobileMenuOpen ? " open" : ""}`}>
        {mobileMenuOpen && (
          <>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="p-4 rounded-md mt-4">
              <div className="flex flex-col items-start gap-0.5 mb-6">
                <span className="text-3xl font-black tracking-tighter bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                  parakh.
                </span>
                <span className="text-[10px] font-medium text-gray-500">
                  Powered by Kuberya AI
                </span>
              </div>
              <nav className="flex flex-col gap-4">
                {/* Public routes - always visible */}
                <Button
                  variant="ghost"
                  onClick={() => {
                    navigate("/");
                    setMobileMenuOpen(false);
                  }}
                  className="justify-start text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Home
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    navigate("/about-us");
                    setMobileMenuOpen(false);
                  }}
                  className="justify-start text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  About Us
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    navigate("/community");
                    setMobileMenuOpen(false);
                  }}
                  className="justify-start text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Community
                </Button>
                {/* <Button
                  variant="ghost"
                  onClick={() => {
                    navigate("/pricing");
                    setMobileMenuOpen(false);
                  }}
                  className="justify-start text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Pricing
                </Button> */}

                {/* Auth-only routes */}
                {authenticated && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        navigate("/evaluate");
                        setMobileMenuOpen(false);
                      }}
                      className="justify-start text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Evaluate
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        navigate("/history");
                        setMobileMenuOpen(false);
                      }}
                      className="justify-start text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      History
                    </Button>
                  </>
                )}
                <div className="pt-4 border-t">
                  {authenticated ? (
                    <Button
                      onClick={async () => {
                        await handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full bg-red-600 text-white hover:bg-red-700 transition-all duration-300"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        navigate("/login");
                        setMobileMenuOpen(false);
                      }}
                      className="w-full bg-gradient-to-br from-purple-600 to-blue-500 text-white hover:shadow-lg hover:opacity-90 transition-all duration-300 rounded-full"
                    >
                      Login / Sign Up
                    </Button>
                  )}
                </div>
              </nav>
            </div>
          </>
        )}
      </div>
    </header>
  );
};
